import { Button } from '@/components/ui/button';
import { TaskItemCard } from '@/features/contribution-kit';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { TranscriptionAudioCopy } from './transcription-audio-copy';
import type { AudioEntry } from './types';

type TranscriptionAudioBatchListProps = {
  t: Record<string, unknown>;
  copy: TranscriptionAudioCopy;
  audios: AudioEntry[];
  currentIndex: number;
  page: number;
  limit: number;
  hasMore: boolean;
  isLoading: boolean;
  onSelectAudio: (index: number) => void;
  onNextPage: () => void;
};

/** The audios of the current set: pick one, or ask for the next set. */
export function TranscriptionAudioBatchList({
  t,
  copy,
  audios,
  currentIndex,
  page,
  limit,
  hasMore,
  isLoading,
  onSelectAudio,
  onNextPage,
}: TranscriptionAudioBatchListProps) {
  return (
    <TaskItemCard
      label={copy.batchLabel}
      corner={
        <Button type="button" variant="ghost" size="sm" onClick={onNextPage} disabled={!hasMore || isLoading} className="h-9 text-muted-foreground">
          {copy.nextBatch}
          <Icon name="chevronRight" className="size-4" />
        </Button>
      }
    >
      <ul className="space-y-1.5">
        {audios.map((audio, index) => {
          const isCurrent = index === currentIndex;
          return (
            <li key={audio.id ?? index}>
              <button
                type="button"
                onClick={() => onSelectAudio(index)}
                aria-current={isCurrent ? 'true' : undefined}
                className={cn(
                  'flex min-h-11 w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isCurrent ? 'border-brand/40 bg-brand/[0.06] text-foreground' : 'border-border/60 bg-surface text-muted-foreground hover:border-brand/30 hover:text-foreground',
                )}
              >
                <span className="w-7 shrink-0 text-xs tabular-nums text-faint-foreground">#{(page - 1) * limit + index + 1}</span>
                <span className="min-w-0 flex-1 truncate">{audio.word || audio.langFr || String(t.audio ?? '')}</span>
                {isCurrent ? <Icon name="volume2" className="size-4 shrink-0 text-brand" /> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </TaskItemCard>
  );
}
